import { useCallback, useEffect, useMemo, useState } from "react";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import * as z from "zod";
import { ChevronDown, ChevronUp, Pencil } from "lucide-react";

import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Checkbox } from "@/components/ui/checkbox";
import { Form } from "@/components/ui/form";
import { Switch } from "@/components/ui/switch";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import InputField from "@/components/form/components/InputField";
import { useAuthStore } from "@/store";
import {
  useRolePermissionsQuery,
  useRolesByDesignationQuery,
  useUpdateRoleMutation,
} from "@/hooks/api/role.queries";
import { useAllDesignationsUnfilteredQuery } from "@/hooks/api/designation.queries";
import { PermissionType, RoleType } from "@/services/role.service";

// ─── Schema ──────────────────────────────────────────────────────────────────

const editRoleSchema = z.object({
  name: z.string().min(1, "Role name is required"),
  description: z.string().optional(),
  designation_id: z.string().min(1, "Designation is required"),
});

type EditRoleFormType = z.infer<typeof editRoleSchema>;

// ─── Types ────────────────────────────────────────────────────────────────────

type ModuleGroup = {
  id: string;
  label: string;
  perms: PermissionType[];
};

// ─── Module Card ──────────────────────────────────────────────────────────────

function ModuleCard({
  module,
  checkedIds,
  onToggleAll,
  onToggleOne,
}: {
  module: ModuleGroup;
  checkedIds: Set<string>;
  onToggleAll: (module: ModuleGroup, on: boolean) => void;
  onToggleOne: (permId: string, checked: boolean) => void;
}) {
  const [expanded, setExpanded] = useState(false);
  const allChecked = module.perms.every((p) => checkedIds.has(p.id));
  const someChecked = module.perms.some((p) => checkedIds.has(p.id));

  const subtitle = module.perms
    .map((p) => {
      const a = p.name.split(".").slice(1).join(".");
      return a.charAt(0).toUpperCase() + a.slice(1).replace(/_/g, " ");
    })
    .join(", ");

  return (
    <Card className="overflow-hidden rounded-xl border border-gray-100 shadow-sm">
      <div
        className="flex cursor-pointer items-center gap-4 px-5 py-4"
        onClick={() => setExpanded((v) => !v)}
      >
        <div className="flex min-w-0 flex-1 flex-col">
          <span className="truncate text-sm font-bold text-gray-900">{module.label}</span>
          <span className="truncate text-xs text-gray-400">{subtitle}</span>
        </div>
        <Switch
          checked={allChecked || someChecked}
          onCheckedChange={(on) => onToggleAll(module, on)}
          onClick={(e) => e.stopPropagation()}
          className="shrink-0 data-[state=checked]:bg-indigo-500"
        />
        {expanded ? (
          <ChevronUp className="h-4 w-4 shrink-0 text-gray-400" />
        ) : (
          <ChevronDown className="h-4 w-4 shrink-0 text-gray-400" />
        )}
      </div>
      {expanded && (
        <div className="grid grid-cols-1 gap-x-6 gap-y-4 border-t border-gray-100 bg-gray-50/50 px-5 py-4 sm:grid-cols-2">
          {module.perms.map((perm) => {
            const a = perm.name.split(".").slice(1).join(".");
            const label = a.charAt(0).toUpperCase() + a.slice(1).replace(/_/g, " ");
            return (
              <label key={perm.id} className="flex cursor-pointer items-center gap-2 text-sm text-gray-700">
                <Checkbox
                  checked={checkedIds.has(perm.id)}
                  onCheckedChange={(c) => onToggleOne(perm.id, !!c)}
                  className="rounded border-gray-300"
                />
                {label}
              </label>
            );
          })}
        </div>
      )}
    </Card>
  );
}

// ─── Edit Panel ──────────────────────────────────────────────────────────────

function EditRolePanel({
  role,
  onDone,
}: {
  role: RoleType;
  onDone: () => void;
}) {
  const currentUser = useAuthStore((s) => s.user);
  const { data: assignablePermissions = [], isLoading: isLoadingPerms } =
    useRolePermissionsQuery(currentUser?.role_id);
  const { data: currentRolePerms = [], isFetching: isFetchingCurrent } =
    useRolePermissionsQuery(role.id, true);
  const { data: designations = [] } = useAllDesignationsUnfilteredQuery();
  const updateMutation = useUpdateRoleMutation();

  const form = useForm<EditRoleFormType>({
    resolver: zodResolver(editRoleSchema),
    defaultValues: { name: role.name, description: role.description, designation_id: "" },
  });

  const [checkedIds, setCheckedIds] = useState<Set<string>>(new Set());
  const [initialized, setInitialized] = useState(false);

  // name → UUID for assignable permissions
  const nameToId = useMemo(
    () => new Map(assignablePermissions.map((p) => [p.name, p.id])),
    [assignablePermissions]
  );

  // Group assignable permissions by module
  const modules: ModuleGroup[] = useMemo(() => {
    const dict = new Map<string, ModuleGroup>();
    assignablePermissions.forEach((p) => {
      const mod = p.name.split(".")[0];
      if (!dict.has(mod)) {
        const label = mod.split("_").map((w) => w.charAt(0).toUpperCase() + w.slice(1)).join(" ");
        dict.set(mod, { id: mod, label, perms: [] });
      }
      dict.get(mod)!.perms.push(p);
    });
    return Array.from(dict.values());
  }, [assignablePermissions]);

  // Pre-check current role's permissions once loaded
  useEffect(() => {
    if (!isFetchingCurrent && currentRolePerms.length > 0 && !initialized) {
      const pre = new Set<string>();
      currentRolePerms.forEach((p) => {
        const id = nameToId.get(p.name);
        if (id) pre.add(id);
      });
      setCheckedIds(pre);
      setInitialized(true);
    }
  }, [isFetchingCurrent, currentRolePerms, nameToId, initialized]);

  const handleToggleAll = useCallback((module: ModuleGroup, on: boolean) => {
    setCheckedIds((prev) => {
      const next = new Set(prev);
      module.perms.forEach((p) => (on ? next.add(p.id) : next.delete(p.id)));
      return next;
    });
  }, []);

  const handleToggleOne = useCallback((permId: string, checked: boolean) => {
    setCheckedIds((prev) => {
      const next = new Set(prev);
      checked ? next.add(permId) : next.delete(permId);
      return next;
    });
  }, []);

  const onSubmit = (values: EditRoleFormType) => {
    updateMutation.mutate(
      { roleId: role.id, payload: { ...values, permission_ids: Array.from(checkedIds) } },
      { onSuccess: onDone }
    );
  };

  const isLoading = isLoadingPerms || isFetchingCurrent;
  const isPending = updateMutation.isPending;

  return (
    <Form {...form}>
      <form onSubmit={form.handleSubmit(onSubmit)} className="flex flex-col gap-5">
        <Card className="overflow-hidden rounded-2xl border-none shadow-sm">
          <CardContent className="flex flex-col gap-4 p-6">
            <InputField<EditRoleFormType>
              name="name"
              label="Role Name"
              type="text"
              disabled={isPending}
              required
            />
            <InputField<EditRoleFormType>
              name="description"
              label="Description"
              type="text"
              disabled={isPending}
            />
            <div className="flex flex-col gap-1.5">
              <label className="flex items-center gap-1 text-sm font-medium text-gray-700">
                Designation <span className="text-destructive">*</span>
              </label>
              <Select
                value={form.watch("designation_id")}
                onValueChange={(v) => form.setValue("designation_id", v, { shouldDirty: true, shouldValidate: true })}
                disabled={isPending}
              >
                <SelectTrigger className="w-full rounded-xl border-gray-200 bg-white">
                  <SelectValue placeholder="Select designation..." />
                </SelectTrigger>
                <SelectContent>
                  {designations.map((d) => (
                    <SelectItem key={d.id} value={d.id}>
                      {d.name.replace(/_/g, " ").replace(/\b\w/g, (c) => c.toUpperCase())}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              {form.formState.errors.designation_id && (
                <p className="text-xs text-destructive">{form.formState.errors.designation_id.message}</p>
              )}
            </div>
          </CardContent>
        </Card>

        {isLoading ? (
          <p className="py-6 text-center text-sm text-gray-400">Loading permissions...</p>
        ) : (
          <div className="flex flex-col gap-3">
            {modules.map((mod) => (
              <ModuleCard
                key={mod.id}
                module={mod}
                checkedIds={checkedIds}
                onToggleAll={handleToggleAll}
                onToggleOne={handleToggleOne}
              />
            ))}
          </div>
        )}

        <div className="flex justify-end gap-3 border-t border-gray-100 pt-4">
          <Button type="button" variant="ghost" onClick={onDone} disabled={isPending}>
            Cancel
          </Button>
          <Button
            type="submit"
            disabled={isPending}
            className="h-9 rounded-md bg-black px-8 text-xs font-bold text-white shadow-sm hover:bg-gray-800"
          >
            {isPending ? "Saving..." : "Save Changes"}
          </Button>
        </div>
      </form>
    </Form>
  );
}

// ─── Role Card ────────────────────────────────────────────────────────────────

function RoleCard({ role, onEdit }: { role: RoleType; onEdit: () => void }) {
  return (
    <Card className="overflow-hidden rounded-xl border border-gray-100 shadow-sm">
      <div className="flex items-center justify-between px-5 py-4">
        <div className="flex flex-col gap-0.5">
          <span className="text-sm font-bold text-gray-900">{role.name}</span>
          {role.description && (
            <span className="text-xs text-gray-400 line-clamp-1">{role.description}</span>
          )}
        </div>
        <Button
          variant="ghost"
          size="sm"
          onClick={onEdit}
          className="h-8 gap-1.5 rounded-lg text-xs text-indigo-600 hover:bg-indigo-50"
        >
          <Pencil className="h-3.5 w-3.5" />
          Edit
        </Button>
      </div>
    </Card>
  );
}

// ─── Main Component ──────────────────────────────────────────────────────────

const ManageRolesTab = () => {
  const { data: designations = [] } = useAllDesignationsUnfilteredQuery();
  const [filterDesignationId, setFilterDesignationId] = useState<string | undefined>(undefined);
  const [editingRole, setEditingRole] = useState<RoleType | null>(null);

  const { data: roles = [], isLoading } = useRolesByDesignationQuery(filterDesignationId);

  if (editingRole) {
    return (
      <div className="flex flex-col gap-4">
        <div className="flex items-center gap-3">
          <button
            onClick={() => { setEditingRole(null); }}
            className="text-xs font-medium text-gray-400 hover:text-gray-700"
          >
            ← Back to roles
          </button>
          <span className="text-sm font-bold text-gray-800">Editing: {editingRole.name}</span>
        </div>
        <EditRolePanel role={editingRole} onDone={() => setEditingRole(null)} />
      </div>
    );
  }

  return (
    <div className="flex flex-col gap-5">
      {/* Designation Filter */}
      <div className="flex flex-col gap-1.5">
        <label className="text-xs font-semibold uppercase tracking-wide text-gray-500">
          Filter by Designation
        </label>
        <Select
          value={filterDesignationId ?? "__all__"}
          onValueChange={(v) => setFilterDesignationId(v === "__all__" ? undefined : v)}
        >
          <SelectTrigger className="w-full rounded-xl border-gray-200 bg-white shadow-sm">
            <SelectValue placeholder="All designations" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="__all__">All Designations</SelectItem>
            {designations.map((d) => (
              <SelectItem key={d.id} value={d.id}>
                {d.name.replace(/_/g, " ").replace(/\b\w/g, (c) => c.toUpperCase())}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>

      {/* Role List */}
      {isLoading ? (
        <p className="py-10 text-center text-sm text-gray-400">Loading roles...</p>
      ) : roles.length === 0 ? (
        <p className="py-10 text-center text-sm text-gray-400">
          No roles found for this designation.
        </p>
      ) : (
        <div className="flex flex-col gap-3">
          {roles.map((role) => (
            <RoleCard key={role.id} role={role} onEdit={() => setEditingRole(role)} />
          ))}
        </div>
      )}
    </div>
  );
};

export default ManageRolesTab;
